import _, {map} from 'lodash';
import {useCallback} from 'react';

import {useLazyUnmountCancelToken} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ExtendedError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {ContactSourcesEnum} from '../../../libs/shared/core/src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../libs/shared/core/src/models/api/recipientModel';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownFetcher} from '../../../src/components/common/dropdowns/source/asyncDropdownSource';
import {buildVisualRecipientFromMatch} from '../../../src/helpers/models/recipientHelpers';
import {
  ApiRecipientMatches,
  RecipientGroupMatchModel,
  RecipientMatchTypesEnum,
} from '../../../src/models/api/recipientMatchModel';
import {ApiGroupVisualRecipients, VisualRecipientModel} from '../../../src/models/api/visualRecipientModel';

export interface RecipientsFetcherProps {
  company: CompanyModel;
  namespace: string;
  source: ContactSourcesEnum;
  role: RecipientRolesEnum;
  enrichVisualRecipient?: (recipient: VisualRecipientModel) => VisualRecipientModel;
  shouldShowMoreAutocompleteResults?: boolean;
  shouldExcludeGroups?: boolean;
}

export function useRecipientsFetcher(props: RecipientsFetcherProps) {
  const {company, namespace, source, role, shouldShowMoreAutocompleteResults, shouldExcludeGroups} = props;
  const visualRecipientEnricher = props.enrichVisualRecipient ?? _.identity;
  const companyUrl = company.url;

  const fetcher: DropdownFetcher<VisualRecipientModel | RecipientGroupMatchModel> = useCallback(
    async (query, _items, cancelToken) => {
      // If the query is empty, just update the state.
      if (!query) {
        return {items: [], canLoadMore: false};
      }

      const maxDisplayedItems = shouldShowMoreAutocompleteResults ? 8 : 6;
      const endpointName = shouldExcludeGroups ? 'search_contact2' : 'search_contact';

      const encodedQuery = encodeURIComponent(query.toLowerCase());
      const path = `${companyUrl}/${endpointName}/${source}/${encodedQuery}?namespace=${namespace}&bump_most_recent=true`;
      const matches = await fetchApiAsync(ApiRecipientMatches, path, {cancelToken});

      // Map the recipient matches to Visual Recipients (easier to deal with).
      const allRecipients = _(matches)
        .map(
          (m) =>
            m.type === RecipientMatchTypesEnum.CONTACT && buildVisualRecipientFromMatch(namespace, role, m),
        )
        .compact()
        .map((vr) => visualRecipientEnricher(vr))
        .compact()
        .value();

      // Limit the number of groups depending on the number of recipients.
      const maxGroups = Math.max(maxDisplayedItems - allRecipients.length, 2);
      const groups = _(matches)
        .map((m) => m.type === RecipientMatchTypesEnum.GROUP && m)
        .compact()
        .take(maxGroups)
        .value();

      // Cap the recipients so that we end up with `maxDisplayedItems` results.
      const recipients = _(allRecipients)
        .uniqBy((r) => r.contact.uid)
        .take(maxDisplayedItems - groups.length)
        .value();

      return {
        items: [...recipients, ...groups],
        canLoadMore: false,
      };
    },
    [
      companyUrl,
      source,
      namespace,
      role,
      visualRecipientEnricher,
      shouldShowMoreAutocompleteResults,
      shouldExcludeGroups,
    ],
  );

  return {fetcher};
}

export interface RecipientsInsertProps {
  company: CompanyModel;
  namespace: string;
  source: ContactSourcesEnum;
  role: RecipientRolesEnum;
  query: string;
  onRecipientsSelect: (arg: ReadonlyArray<VisualRecipientModel>) => void;
}

export function useInsertRecipientOrGroup(props: RecipientsInsertProps) {
  const {company, source, role, query, onRecipientsSelect} = props;
  const companyUrl = company.url;

  // Recipient event handler.
  const onRecipientSelect = useCallback(
    (recipient: VisualRecipientModel, position?: number) => {
      onRecipientsSelect([recipient]);

      if (position) {
        trackInterfaceEvent(InterfaceEventsEnum.RECIPIENTS_AUTOSUGGEST_CLICK, {
          recipient_type: 'recipient',
          position,
          query_length: query.length,
        });
      }
    },
    [onRecipientsSelect, query.length],
  );

  // Group event handler.
  const buildUnmountCancelToken = useLazyUnmountCancelToken();
  const onGroupSelect = useCallback(
    async (group: RecipientGroupMatchModel, position?: number) => {
      try {
        // Fetch the recipients for this group.
        const path = `${companyUrl}/card_groups/${group.id}/${source}/contacts?limit=1000`;
        const recipients = await fetchApiAsync(ApiGroupVisualRecipients, path, {
          cancelToken: buildUnmountCancelToken(),
        });

        // Remap their role and insert them.
        const recipientsWithRole = map(recipients, (r) => ({...r, role}));
        onRecipientsSelect(recipientsWithRole);

        if (position) {
          trackInterfaceEvent(InterfaceEventsEnum.RECIPIENTS_AUTOSUGGEST_CLICK, {
            recipient_type: 'group',
            position,
            query_length: query.length,
          });
        }
      } catch (error) {
        reportToBugsnag(new ExtendedError('Failed to fetch card group.', error));
      }
    },
    [buildUnmountCancelToken, companyUrl, onRecipientsSelect, query.length, role, source],
  );

  return {onRecipientSelect, onGroupSelect};
}
